import { Grid, Typography, Box, LinearProgress, Chip } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { UrlApi } from "../../../services/apirest";

const STATUS = {
  ok:       { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Al día"   },
  warning:  { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Atención"  },
  critical: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Crítico"   },
};

function getStatus(section) {
  const p = (section.ordenes_pendientes ?? 0) + (section.correctivos_pendientes ?? 0);
  if (p === 0) return "ok";
  if (p <= 3)  return "warning";
  return "critical";
}

function MetricSection({ icon, label, pending, done }) {
  const total  = pending + done;
  const pct    = total > 0 ? Math.round((done / total) * 100) : 0;
  const barClr = pct === 100 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626";

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {icon}
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8" }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: barClr }}>
          {done}/{total}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 3, borderRadius: 1.5, mb: 1,
          bgcolor: "#f1f5f9",
          "& .MuiLinearProgress-bar": { bgcolor: barClr, borderRadius: 1.5 },
        }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PendingActionsIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            <Box component="span" sx={{ color: "#d97706", fontWeight: 700 }}>{pending}</Box>
            {" "}pendiente{pending !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AssignmentTurnedInIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
          <Typography sx={{ fontSize: 11, color: "#64748b" }}>
            <Box component="span" sx={{ color: "#16a34a", fontWeight: 700 }}>{done}</Box>
            {" "}realizado{done !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function PlantSectionsList() {
  const [plantas, setPlantas] = useState([]);

  const getPlantas = useCallback(async () => {
    await axios
      .get(UrlApi + "/mp_equipos/countbyplantas")
      .then((res) => {
        const suc_auth = localStorage.getItem("suc_auth");
        if (!suc_auth) return;
        const ids = suc_auth.split(",").map((id) => parseInt(id.trim(), 10));
        setPlantas(res.data.filter((item) => ids.includes(parseInt(item.id_planta, 10))));
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => { getPlantas(); }, [getPlantas]);

  return (
    <Grid container spacing={2.5} sx={{ mb: 5 }}>
      {plantas.map((section) => {
        const status = getStatus(section);
        const st = STATUS[status];

        return (
          <Grid item xs={12} md={6} lg={4} key={section.id_planta}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              {/* Accent line */}
              <Box sx={{ height: 3, bgcolor: st.color }} />

              <Box sx={{ p: 2.5 }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#94a3b8", mb: 0.25 }}>
                      Planta
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1e293b", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {section.sucursal}
                    </Typography>
                  </Box>
                  <Chip
                    label={st.label}
                    size="small"
                    sx={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                      color: st.color, bgcolor: st.bg,
                      border: `1px solid ${st.border}`,
                      height: 22, flexShrink: 0,
                    }}
                  />
                </Box>

                {/* Equipment count */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  py: 1, px: 1.5, mb: 2,
                  borderRadius: 1.5,
                  bgcolor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                }}>
                  <BusinessIcon sx={{ fontSize: 15, color: "#3b82f6", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1 }}>
                    <Box component="span" sx={{ fontSize: 20, fontWeight: 800, color: "#1e293b", mr: 0.6, lineHeight: 1 }}>
                      {section.equipos_planta ?? 0}
                    </Box>
                    equipos registrados
                  </Typography>
                </Box>

                <Box sx={{ height: "1px", bgcolor: "#f1f5f9", mb: 2 }} />

                {/* Órdenes de trabajo */}
                <Box sx={{ mb: 2 }}>
                  <MetricSection
                    icon={<TaskAltIcon sx={{ fontSize: 12, color: "#3b82f6" }} />}
                    label="Órdenes de trabajo"
                    pending={section.ordenes_pendientes ?? 0}
                    done={section.ordenes_realizadas ?? 0}
                  />
                </Box>

                <Box sx={{ height: "1px", bgcolor: "#f1f5f9", mb: 2 }} />

                {/* Correctivos */}
                <MetricSection
                  icon={<BuildCircleIcon sx={{ fontSize: 12, color: "#8b5cf6" }} />}
                  label="Correctivos"
                  pending={section.correctivos_pendientes ?? 0}
                  done={section.correctivos_finalizados ?? 0}
                />
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
